import {
  getLDAnonymousId,
  getLDFlags,
  type LDAnonymousId,
  type LDFlagValue,
  type LDFlags,
} from 'public/assets/js/analytics/launchDarkly';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from 'react';
import { Events } from 'src/lib/trackingEvents';
import { useTrack } from '../AnalyticsContext';

const LDContext = createContext<{
  anonymousId: LDAnonymousId;
  flags: LDFlags;
  trackViewed: (flag: string, variant: LDFlagValue) => void;
}>(null);

export const LDProvider: FC<PropsWithChildren> = ({ children }) => {
  const [anonymousId] = useState(() => {
    return getLDAnonymousId();
  });
  const [flags] = useState(() => {
    return getLDFlags();
  });
  const track = useTrack();
  const trackedFlags = useRef(new Set<string>());

  const trackViewed = useCallback(
    (flag: string, variant: LDFlagValue) => {
      if (!track || trackedFlags.current.has(flag)) {
        return;
      }

      track({
        type: Events.VIEWED_EXPERIMENT,
        payload: {
          experimentName: flag,
          experimentVariant: variant,
        },
      });

      trackedFlags.current.add(flag);
    },
    [track],
  );

  const contextValue = useMemo(() => {
    return {
      anonymousId,
      flags,
      trackViewed,
    };
  }, [anonymousId, flags, trackViewed]);

  return (
    <LDContext.Provider value={contextValue}>{children}</LDContext.Provider>
  );
};

export function useLD() {
  return useContext(LDContext);
}

export function useLDFlag(flag: string, fallbackValue?: LDFlagValue) {
  // Shared components can render outside LDProvider, so a missing provider
  // has to read as "no flags" rather than throw.
  const ld = useLD();
  const evaluatedValue = ld?.flags[flag] ?? null;
  const trackViewed = ld?.trackViewed;

  useEffect(() => {
    if (evaluatedValue === null || !trackViewed) {
      return;
    }

    trackViewed(flag, evaluatedValue);
  }, [flag, evaluatedValue, trackViewed]);

  return evaluatedValue ?? fallbackValue ?? null;
}
